/**
 * FullCalendar event interaction handlers for manual scheduling.
 * Handles click/drag selection, event drop, and event resize.
 * Click uses previous duration; drag uses actual dragged duration.
 *
 * Ref: KInterviewScheduler/.../schEventHandlers.ts
 */
import { useCallback, useEffect, useRef } from 'react';
import type FullCalendar from '@fullcalendar/react';
import moment from 'moment-timezone';
import {
  DRAG_THRESHOLD_PX,
  SLOT_DURATION_MINUTES,
  MAX_DURATION_MINUTES,
  calcDurationMinutes,
} from '../events';

interface UseCalendarEventHandlersOptions {
  calendarRef: React.RefObject<FullCalendar | null>;
  timezone: string;
  duration: number;
  onSlotSelect: (_slot: { start: string; end: string }) => void;
}

export function useCalendarEventHandlers({
  calendarRef,
  timezone,
  duration,
  onSlotSelect,
}: UseCalendarEventHandlersOptions) {
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);

  // Track mouse down position for click vs drag detection
  useEffect(() => {
    const el = calendarRef.current?.getApi()?.el;
    if (!el) return;

    const handleMouseDown = (e: MouseEvent) => {
      mouseDownPos.current = { x: e.clientX, y: e.clientY };
    };
    el.addEventListener('mousedown', handleMouseDown);
    return () => el.removeEventListener('mousedown', handleMouseDown);
  }, [calendarRef]);

  // Click/drag selection → create or replace booked slot
  const handleSelect = useCallback(
    (info: {
      startStr: string;
      endStr: string;
      jsEvent?: MouseEvent | null;
    }) => {
      const selectedDuration = calcDurationMinutes(info.startStr, info.endStr);

      // Detect click vs drag
      const mouseUp = info.jsEvent
        ? { x: info.jsEvent.clientX, y: info.jsEvent.clientY }
        : null;
      const dx =
        mouseDownPos.current && mouseUp
          ? Math.abs(mouseUp.x - mouseDownPos.current.x)
          : 0;
      const dy =
        mouseDownPos.current && mouseUp
          ? Math.abs(mouseUp.y - mouseDownPos.current.y)
          : 0;
      const wasDragged = dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX;
      const isClick = !wasDragged && selectedDuration <= SLOT_DURATION_MINUTES;

      // Click: use configured duration. Drag: use actual dragged duration.
      let newDuration = isClick ? duration : selectedDuration;
      if (newDuration > MAX_DURATION_MINUTES) {
        newDuration = MAX_DURATION_MINUTES;
      }

      const end = isClick
        ? moment
            .tz(info.startStr, timezone)
            .add(duration, 'minutes')
            .toISOString()
        : info.endStr;

      onSlotSelect({ start: info.startStr, end });
      // Clear FullCalendar's drag-select highlight (`.fc-highlight`).
      // Without this, the dashed selection box stays painted at the
      // click location even after the booked event renders on top —
      // visible as a faint pill-shaped halo "behind" the booked event,
      // particularly noticeable for sub-15-min slots where the booked
      // event's height doesn't fully cover the highlight rectangle.
      // SelfScheduleCalendar already calls this; manual mode never did.
      calendarRef.current?.getApi()?.unselect();
      mouseDownPos.current = null;
    },
    [calendarRef, duration, timezone, onSlotSelect]
  );

  // Drag-move booked event
  const handleEventDrop = useCallback(
    (info: {
      event: { startStr: string; endStr: string };
      revert: () => void;
    }) => {
      const start = moment.tz(info.event.startStr, timezone);
      if (start.isBefore(moment().tz(timezone))) {
        info.revert();
        return;
      }

      const dur = calcDurationMinutes(info.event.startStr, info.event.endStr);
      if (dur <= 0 || dur > MAX_DURATION_MINUTES) {
        info.revert();
        return;
      }

      onSlotSelect({
        start: info.event.startStr,
        end: info.event.endStr,
      });
    },
    [timezone, onSlotSelect]
  );

  // Resize booked event
  const handleEventResize = useCallback(
    (info: {
      event: { startStr: string; endStr: string };
      revert: () => void;
    }) => {
      const dur = calcDurationMinutes(info.event.startStr, info.event.endStr);
      if (dur <= 0 || dur > MAX_DURATION_MINUTES) {
        info.revert();
        return;
      }

      onSlotSelect({
        start: info.event.startStr,
        end: info.event.endStr,
      });
    },
    [onSlotSelect]
  );

  return { handleSelect, handleEventDrop, handleEventResize };
}
